import React, { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Divider,
  Drawer,
  Empty,
  Spin,
  Tabs,
  Typography,
} from "antd";
import {
  AlertOutlined,
  BankOutlined,
  BellOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CommentOutlined,
  CustomerServiceOutlined,
  DollarOutlined,
  FileAddOutlined,
  FileTextOutlined,
  MedicineBoxOutlined,
  MessageOutlined,
  UserAddOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

// ── Icon + color per notification type ────────────────────────────────────────
const TYPE_CONFIG = {
  NEW_CHAT_MESSAGE:           { Icon: MessageOutlined,         color: "#1890ff" },
  NEW_CASE_REPLY:             { Icon: CommentOutlined,         color: "#1890ff" },
  NEW_CASE_REQUEST:           { Icon: FileAddOutlined,         color: "#722ed1" },
  SUPPORT_TICKET_RESPONSE:    { Icon: CustomerServiceOutlined, color: "#52c41a" },
  PRESCRIPTION_STATUS_CHANGE: { Icon: MedicineBoxOutlined,     color: "#722ed1" },
  PATIENT_ENROLLED:           { Icon: UserAddOutlined,         color: "#13c2c2" },
  APPOINTMENT_BOOKED:         { Icon: CalendarOutlined,        color: "#fa8c16" },
  APPOINTMENT_CONFIRMED:      { Icon: CalendarOutlined,        color: "#52c41a" },
  APPOINTMENT_REJECTED:       { Icon: CalendarOutlined,        color: "#f5222d" },
  APPOINTMENT_CANCELLED:      { Icon: CalendarOutlined,        color: "#8c8c8c" },
  EMS_UPDATE:                 { Icon: AlertOutlined,           color: "#f5222d" },
  DIAGNOSIS_ADDED:            { Icon: FileTextOutlined,        color: "#8c8c8c" },
  USER_TICKET:                { Icon: CustomerServiceOutlined, color: "#fa8c16" },
  ADMIN_RESPONSE:             { Icon: CheckCircleOutlined,     color: "#52c41a" },
  BILLING_ACTIVITY:           { Icon: FileTextOutlined,        color: "#1890ff" },
  NEW_ESCALATION:             { Icon: AlertOutlined,           color: "#f5222d" },
  NEW_ORG_TICKET:             { Icon: MessageOutlined,         color: "#1890ff" },
  NEW_PARTNER_REGISTRATION:   { Icon: BankOutlined,            color: "#52c41a" },
  HIGH_BILLING_ACTIVITY:      { Icon: DollarOutlined,          color: "#fa8c16" },
  // Partner org notification types
  NEW_PICKUP_REQUEST:         { Icon: MedicineBoxOutlined,     color: "#722ed1" },
  PICKUP_CANCELLED:           { Icon: AlertOutlined,           color: "#fa8c16" },
  PRESCRIPTION_DISPENSED:     { Icon: CheckCircleOutlined,     color: "#52c41a" },
};

const TICKET_TYPES = new Set([
  "USER_TICKET", "ADMIN_RESPONSE", "SUPPORT_TICKET_RESPONSE",
  "NEW_ESCALATION", "NEW_ORG_TICKET",
]);
const BILLING_TYPES = new Set([
  "BILLING_ACTIVITY", "HIGH_BILLING_ACTIVITY",
]);
const SYSTEM_TYPES = new Set([
  "NEW_PARTNER_REGISTRATION", "PATIENT_ENROLLED", "PRESCRIPTION_STATUS_CHANGE",
  "APPOINTMENT_BOOKED", "EMS_UPDATE", "DIAGNOSIS_ADDED",
  "NEW_CHAT_MESSAGE", "NEW_CASE_REPLY", "NEW_CASE_REQUEST",
]);

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.max(0, Date.now() - d.getTime());
  const s = Math.floor(diff / 1000);
  if (s < 45) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const days = Math.floor(h / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString();
}

function NotifIcon({ type }) {
  const cfg = TYPE_CONFIG[type] ?? { Icon: BellOutlined, color: "#8c8c8c" };
  return (
    <div style={{
      width: 36, height: 36, borderRadius: 18, flexShrink: 0,
      background: `${cfg.color}1a`,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <cfg.Icon style={{ fontSize: 16, color: cfg.color }} />
    </div>
  );
}

const EMPTY_CONFIG = {
  all:     { icon: <CustomerServiceOutlined style={{ fontSize: 32, color: "#d9d9d9" }} />, text: "You're all caught up!" },
  unread:  { icon: <CheckCircleOutlined     style={{ fontSize: 32, color: "#d9d9d9" }} />, text: "No unread notifications" },
  tickets: { icon: <CustomerServiceOutlined style={{ fontSize: 32, color: "#d9d9d9" }} />, text: "No ticket notifications" },
  billing: { icon: <DollarOutlined          style={{ fontSize: 32, color: "#d9d9d9" }} />, text: "No billing notifications" },
  system:  { icon: <BellOutlined            style={{ fontSize: 32, color: "#d9d9d9" }} />, text: "No system notifications" },
};

export default function NotificationPanel({
  visible,
  onClose,
  notifications = [],
  unreadCount = 0,
  loading = false,
  hasMore = false,
  onMarkAllRead,
  onItemClick,
  onLoadMore,
  tabsConfig,   // optional: [{ key, label, filter }] — overrides default tabs
}) {
  const [activeTab, setActiveTab] = useState("all");

  const filtered = useMemo(() => {
    if (tabsConfig) {
      const tab = tabsConfig.find(t => t.key === activeTab);
      if (!tab?.filter) return notifications;
      return notifications.filter(tab.filter);
    }
    switch (activeTab) {
      case "unread":  return notifications.filter(n => !n.is_read);
      case "tickets": return notifications.filter(n => TICKET_TYPES.has(n.notification_type));
      case "billing": return notifications.filter(n => BILLING_TYPES.has(n.notification_type));
      case "system":  return notifications.filter(n => SYSTEM_TYPES.has(n.notification_type));
      default:        return notifications;
    }
  }, [notifications, activeTab, tabsConfig]);

  const tabItems = tabsConfig
    ? tabsConfig.map(t => ({
        key: t.key,
        label: t.key === "unread" && unreadCount > 0
          ? <Badge count={unreadCount} size="small" offset={[6, -2]}>{t.label}</Badge>
          : t.label,
      }))
    : [
        { key: "all",     label: "All" },
        {
          key: "unread",
          label: unreadCount > 0
            ? <Badge count={unreadCount} size="small" offset={[6, -2]}>Unread</Badge>
            : "Unread",
        },
        { key: "tickets", label: "Tickets" },
        { key: "billing", label: "Billing" },
        { key: "system",  label: "System" },
      ];

  const emptyState = EMPTY_CONFIG[activeTab] ?? EMPTY_CONFIG.all;

  const header = (
    <>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", paddingBottom: 4 }}>
        <div>
          <Text strong style={{ fontSize: 18, display: "block" }}>Notifications</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
          </Text>
        </div>
        {unreadCount > 0 && (
          <Button type="link" size="small" style={{ padding: 0, marginTop: 4 }} onClick={onMarkAllRead}>
            Mark all as read
          </Button>
        )}
      </div>
      <Divider style={{ margin: "8px 0 0" }} />
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="small"
        style={{ marginBottom: 0 }}
        tabBarStyle={{ marginBottom: 0 }}
      />
    </>
  );

  return (
    <Drawer
      title={header}
      placement="right"
      width={420}
      open={visible}
      onClose={onClose}
      mask={false}
      styles={{ header: { paddingBottom: 0 }, body: { padding: 0, display: "flex", flexDirection: "column" } }}
      destroyOnHidden
      closable
    >
      <Spin spinning={loading} style={{ flex: 1 }}>
        <div style={{ flex: 1, overflowY: "auto", maxHeight: "calc(100vh - 220px)" }}>
          {filtered.length === 0 ? (
            <Empty
              image={emptyState.icon}
              description={<Text type="secondary">{emptyState.text}</Text>}
              style={{ padding: "40px 0" }}
            />
          ) : (
            filtered.map(n => {
              const preview = n.preview ?? n.message;
              return (
                <div
                  key={n.id}
                  onClick={() => onItemClick?.(n)}
                  onMouseEnter={e => { e.currentTarget.style.background = n.is_read ? "#f5f5f5" : "#dbeeff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = n.is_read ? "#fff" : "#eff6ff"; }}
                  style={{
                    padding: "12px 16px",
                    cursor: "pointer",
                    borderBottom: "1px solid #f0f0f0",
                    background: n.is_read ? "#fff" : "#eff6ff",
                    display: "flex",
                    gap: 12,
                    alignItems: "flex-start",
                    transition: "background 0.15s",
                  }}
                >
                  <NotifIcon type={n.notification_type} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      strong={!n.is_read}
                      style={{ display: "block", fontSize: 13, color: n.is_read ? "#595959" : "#1f1f1f" }}
                    >
                      {n.title}
                    </Text>
                    {preview && (
                      <Text
                        type="secondary"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          fontSize: 12,
                          lineHeight: "1.4",
                          marginTop: 2,
                        }}
                      >
                        {preview}
                      </Text>
                    )}
                    <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: "block" }}>
                      {relativeTime(n.created_at)}
                    </Text>
                  </div>
                  {!n.is_read && (
                    <div style={{
                      width: 8, height: 8, borderRadius: 4,
                      background: "#1677ff", marginTop: 6, flexShrink: 0,
                    }} />
                  )}
                </div>
              );
            })
          )}
        </div>
      </Spin>

      {hasMore && (
        <div style={{ padding: "12px 16px", textAlign: "center", borderTop: "1px solid #f0f0f0" }}>
          <Button type="link" size="small" onClick={onLoadMore} loading={loading}>
            Load more
          </Button>
        </div>
      )}

      <div style={{
        padding: "8px 16px",
        borderTop: "1px solid #f0f0f0",
        background: "#fafafa",
        textAlign: "center",
      }}>
        <Text type="secondary" style={{ fontSize: 11 }}>Notifications are kept for 30 days</Text>
      </div>
    </Drawer>
  );
}
